import React, { useEffect, useState } from 'react';
import api from '../../utils/api';

const ExternalIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
  </svg>
);

const SUB_STATE = {
  NEW: { label: 'Not started', bg: 'bg-gray-100', text: 'text-gray-600' },
  CREATED: { label: 'Assigned', bg: 'bg-gray-100', text: 'text-gray-600' },
  TURNED_IN: { label: 'Turned in', bg: 'bg-brand-50', text: 'text-brand-600' },
  RETURNED: { label: 'Graded', bg: 'bg-green-50', text: 'text-green-700' },
  RECLAIMED_BY_STUDENT: { label: 'Reclaimed', bg: 'bg-yellow-50', text: 'text-yellow-700' },
};

const StudentClassroom = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [activeTab, setActiveTab] = useState('assignments');

  useEffect(() => {
    const fetchEnrollments = async () => {
      try {
        const { data } = await api.get('/enroll/my');
        // Only show courses synced to Classroom
        const withClassroom = (data.enrollments || []).filter(
          e => e.status === 'active' && e.course?.googleClassroom?.courseId
        );
        setEnrollments(withClassroom);
      } catch (err) {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    fetchEnrollments();
  }, []);

  if (loading) return <div className="text-gray-500 text-sm">Loading...</div>;

  // Course detail view
  if (selectedCourse) {
    return (
      <CourseClassroomView
        enrollment={selectedCourse}
        onBack={() => setSelectedCourse(null)}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
    );
  }

  // Course list
  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Classroom</h1>

      {/* Coming Soon banner */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center mb-6">
        <div className="w-14 h-14 rounded-full bg-yellow-50 flex items-center justify-center mx-auto mb-4">
          <svg className="w-7 h-7 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Google Classroom Integration</h3>
        <p className="text-sm text-gray-500 mb-1">Coming soon! View assignments, submit homework, and check grades — all from DanceHall.</p>
        <p className="text-sm text-gray-400 mb-4">In the meantime, access your classroom directly.</p>
        <a
          href="https://classroom.google.com"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-brand-500 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-brand-600 transition-colors"
        >
          Open Google Classroom
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
          </svg>
        </a>
      </div>

      {enrollments.length === 0 ? (
        null
      ) : (
        <div className="space-y-3">
          {enrollments.map(enrollment => {
            const course = enrollment.course;
            return (
              <div
                key={enrollment._id}
                onClick={() => { setSelectedCourse(enrollment); setActiveTab('assignments'); }}
                className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 cursor-pointer hover:border-brand-200 hover:shadow-md transition-all"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">{course.name}</h3>
                    <p className="text-sm text-gray-500">
                      {course.danceStyle}{course.levelName ? ` \u00b7 ${course.levelName}` : ''}
                    </p>
                  </div>
                  <span className="text-sm text-brand-500 font-medium">View &rarr;</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// Course Classroom View for Students
// ═══════════════════════════════════════════════════════════════════

const TABS = [
  { key: 'assignments', label: 'Assignments & Grades' },
  { key: 'announcements', label: 'Announcements' },
];

const CourseClassroomView = ({ enrollment, onBack, activeTab, setActiveTab }) => {
  const course = enrollment.course;

  return (
    <div>
      <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-700 mb-4 inline-flex items-center gap-1">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
        </svg>
        All Courses
      </button>

      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{course.name}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {course.danceStyle}{course.levelName ? ` \u00b7 ${course.levelName}` : ''}
          </p>
        </div>
        {course.googleClassroom?.courseUrl && (
          <a
            href={course.googleClassroom.courseUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-brand-500 hover:text-brand-600 font-medium flex items-center gap-1"
          >
            Open in Classroom <ExternalIcon />
          </a>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-brand-500 text-brand-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'assignments' && <StudentAssignmentsTab courseId={course._id} />}
      {activeTab === 'announcements' && <StudentAnnouncementsTab courseId={course._id} />}
    </div>
  );
};

// ─── Student Assignments Tab ─────────────────────────────────────

const StudentAssignmentsTab = ({ courseId }) => {
  const [assignments, setAssignments] = useState([]);
  const [classroomUrl, setClassroomUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await api.get(`/google/classroom/student/coursework/${courseId}`);
        setAssignments(data.courseWork || []);
        setClassroomUrl(data.classroomUrl || '');
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load assignments');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [courseId]);

  if (loading) return <div className="text-gray-500 text-sm">Loading assignments...</div>;
  if (error) return <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>;

  if (assignments.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
        <p className="text-gray-500">No assignments posted yet.</p>
      </div>
    );
  }

  // Summary stats
  const graded = assignments.filter(a => a.submission?.assignedGrade !== undefined);
  const totalPoints = graded.reduce((sum, a) => sum + (a.maxPoints || 100), 0);
  const earnedPoints = graded.reduce((sum, a) => sum + (a.submission?.assignedGrade || 0), 0);
  const avgGrade = totalPoints > 0 ? Math.round((earnedPoints / totalPoints) * 100) : null;

  return (
    <div>
      {/* Grade summary */}
      {graded.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 mb-6">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-gray-900">{graded.length}</div>
              <div className="text-sm text-gray-500">Graded</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-brand-500">{avgGrade}%</div>
              <div className="text-sm text-gray-500">Average</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{earnedPoints}/{totalPoints}</div>
              <div className="text-sm text-gray-500">Total Points</div>
            </div>
          </div>
        </div>
      )}

      {/* Assignment list */}
      <div className="space-y-3">
        {assignments.map(a => {
          const due = a.dueDate
            ? new Date(a.dueDate.year, a.dueDate.month - 1, a.dueDate.day)
            : null;
          const isPast = due && due < new Date();
          const sub = a.submission;
          const stateInfo = sub ? (SUB_STATE[sub.state] || { label: sub.state, bg: 'bg-gray-100', text: 'text-gray-600' }) : null;

          return (
            <div key={a.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900">{a.title}</h3>
                    {stateInfo && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${stateInfo.bg} ${stateInfo.text}`}>
                        {stateInfo.label}
                      </span>
                    )}
                    {sub?.late && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-600">Late</span>
                    )}
                  </div>
                  {a.description && <p className="text-sm text-gray-500 mt-1 line-clamp-2">{a.description}</p>}
                </div>

                {/* Grade display */}
                <div className="text-right ml-4 flex-shrink-0">
                  {sub?.assignedGrade !== undefined && sub?.assignedGrade !== null ? (
                    <div>
                      <div className="text-xl font-bold text-gray-900">{sub.assignedGrade}<span className="text-sm text-gray-400">/{a.maxPoints || 100}</span></div>
                      <div className="text-xs text-gray-400">
                        {Math.round((sub.assignedGrade / (a.maxPoints || 100)) * 100)}%
                      </div>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">{a.maxPoints || 100} pts</span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
                {due && (
                  <span className={isPast ? 'text-red-500' : ''}>
                    Due: {due.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                )}
                {a.alternateLink && (
                  <a
                    href={a.alternateLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-500 hover:text-brand-600 font-medium flex items-center gap-1 ml-auto"
                  >
                    Open in Classroom <ExternalIcon />
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Student Announcements Tab ───────────────────────────────────

const StudentAnnouncementsTab = ({ courseId }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await api.get(`/google/classroom/student/announcements/${courseId}`);
        setAnnouncements(data.announcements || []);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load announcements');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [courseId]);

  if (loading) return <div className="text-gray-500 text-sm">Loading announcements...</div>;
  if (error) return <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>;

  if (announcements.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
        <p className="text-gray-500">No announcements yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {announcements.map(a => (
        <div key={a.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <p className="text-gray-900 whitespace-pre-wrap">{a.text}</p>
          <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
            <span>{new Date(a.creationTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

export default StudentClassroom;
